import { z } from 'zod';
import { IsoDateSchema, SlugInputSchema, SlugSchema, WikiDocumentTypeSchema } from './entities.js';
import { CaseProcedureTypeSchema } from './procedure.js';
import {
  ProceduralActPhaseSchema,
  ProceduralEnqueteKindSchema,
} from './procedural-act.js';
import { StatutProceduralSchema } from './procedural-status.js';
import {
  NullitiesRetryAttemptMetadataSchema,
  NullitiesRetryLineageSchema,
} from './nullities-retry.js';
import { CaseWikiDomainProfileIdSchema } from './profile.js';

/**
 * Case Wiki — content schemas pour `case_wiki_documents` (M056, écrit en M068).
 *
 * Types actifs (les types historiques `phases` / `arguments` /
 * `principal_parties` ont été retirés du contrat le 2026-07-03 — plus aucune
 * surface ne les lit) :
 *
 * - `snapshot` : markdown résumé exécutif (= le résumé narratif du
 *   dossier)
 * - `litigation_review` : JSONB structuré pour la revue des enjeux litigieux
 * - `nullities_admissibility` : JSONB structuré pour les nullités,
 *   irrecevabilités et points de recevabilité à instruire
 *
 * Spec : docs/architecture/case-wiki/data-model.md §3.2.4 + §10.2.
 *
 * Versioning linéaire `(case_id, type)`. Rétention 10 dernières versions
 * (purge applicative dans `insertWikiDocument`).
 */

// ---------------------------------------------------------------------------
// Markdown document content
// ---------------------------------------------------------------------------

/**
 * Métadonnées communes aux documents générés par LLM. Gardées dans chaque
 * content JSONB pour tracer la version sans modifier le schéma SQL.
 */
export const WikiDocumentGenerationMetadataSchema = z.object({
  model: z.string(),
  prompt_tokens: z.number().int().nonnegative().optional(),
  completion_tokens: z.number().int().nonnegative().optional(),
  duration_ms: z.number().int().nonnegative().optional(),
  /** Stable backend publication identity used for crash-safe replay. */
  publication_key: z.string().trim().min(10).max(240).optional(),
  /** Exact server-owned Review lineage used by targeted family retry. */
  nullities_retry_lineage: NullitiesRetryLineageSchema.optional(),
  /** Idempotency/source audit for a document created by targeted retry. */
  nullities_retry_attempt: NullitiesRetryAttemptMetadataSchema.optional(),
});
export type WikiDocumentGenerationMetadata = z.infer<
  typeof WikiDocumentGenerationMetadataSchema
>;

export const NullitiesProtocolReferenceSchema = z.object({
  protocol_ref_id: z.string().trim().min(3).max(180),
  official_label: z.string().trim().min(3).max(240),
  source_url: z.string().url().max(500),
}).strict();

export const NullitiesProtocolReferenceSnapshotSchema = z.object({
  protocol_pack_id: z.string().trim().min(1).max(120),
  protocol_pack_version: z.number().int().positive(),
  references: z.array(NullitiesProtocolReferenceSchema).min(1).max(200),
}).strict().superRefine((value, ctx) => {
  const seen = new Set<string>();
  for (const [index, reference] of value.references.entries()) {
    if (seen.has(reference.protocol_ref_id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['references', index, 'protocol_ref_id'],
        message: `protocol_ref_id '${reference.protocol_ref_id}' must be unique in its protocol snapshot`,
      });
    }
    seen.add(reference.protocol_ref_id);
  }
});
export type NullitiesProtocolReferenceSnapshot = z.infer<
  typeof NullitiesProtocolReferenceSnapshotSchema
>;

/**
 * Wrapper markdown commun aux types narratifs. Stocké en JSONB pour rester
 * extensible.
 */
export const MarkdownDocContentSchema = z.object({
  markdown: z.string().min(50),
  /** Optionnel — utile pour tracer ce qui a généré la version. */
  generation_metadata: WikiDocumentGenerationMetadataSchema.optional(),
});
export type MarkdownDocContent = z.infer<typeof MarkdownDocContentSchema>;

// ---------------------------------------------------------------------------
// `litigation_review` content
// ---------------------------------------------------------------------------

export const LitigationReviewSeveritySchema = z.enum([
  'critical',
  'important',
  'watch',
]);
export type LitigationReviewSeverity = z.infer<typeof LitigationReviewSeveritySchema>;

/**
 * `issue_kind` qu'un agent peut ÉCRIRE dans la revue des points litigieux. Les questions de
 * procédure relèvent de la passe procédurale : `procedural_issue` n'y figure pas.
 */
export const LITIGATION_REVIEW_WRITE_ISSUE_KINDS = [
  'fact_dispute',
  'evidence_gap',
  'legal_issue',
] as const;

/**
 * `issue_kind` LISIBLE : les valeurs d'écriture plus `procedural_issue`, que portent encore des
 * documents historiques. La retirer d'ici rendrait ces documents illisibles.
 */
export const LitigationReviewIssueKindSchema = z.enum([
  ...LITIGATION_REVIEW_WRITE_ISSUE_KINDS,
  'procedural_issue',
]);
export type LitigationReviewIssueKind = z.infer<typeof LitigationReviewIssueKindSchema>;

/**
 * Liste montrée à l'agent dans le schéma de l'outil. Exposer les quatre valeurs de lecture puis
 * refuser `procedural_issue` par une règle invisible revenait à proposer une valeur toujours
 * refusée : l'agent la choisissait, et n'apprenait la règle que par le refus.
 */
const LitigationReviewWriteIssueKindSchema = z.enum(LITIGATION_REVIEW_WRITE_ISSUE_KINDS, {
  errorMap: (_issue, ctx) => ({
    message:
      ctx.data === 'procedural_issue'
        ? 'issue_kind "procedural_issue" est réservé à la passe procédurale (nullités, recevabilité) : retire ce point de la revue des points litigieux'
        : `issue_kind doit valoir ${LITIGATION_REVIEW_WRITE_ISSUE_KINDS.join(' | ')} (reçu : ${JSON.stringify(ctx.data) ?? 'champ absent'})`,
  }),
});

/**
 * Un point de revue exploitable par l'avocat. Les liens restent optionnels car
 * la revue peut pointer vers des faits, pièces ou contradictions selon ce qui
 * existe déjà dans le dossier.
 *
 * `id` est généré automatiquement par le default Zod si absent — l'agent
 * Reviewer ne le fournit pas, et les versions historiques (pré-2026-05-29)
 * stockées sans `id` reçoivent un UUID neuf au prochain parse. Cet identifiant
 * stable permet le drag-drop côté UI et les endpoints PATCH/DELETE par item.
 */
export const LitigationReviewItemSchema = z.object({
  id: z.string().uuid().default(() => crypto.randomUUID()),
  title: z.string().min(5).max(160),
  severity: LitigationReviewSeveritySchema,
  issue_kind: LitigationReviewIssueKindSchema,
  explanation: z.string().min(40).max(900),
  why_it_matters: z.string().min(40).max(900),
  related_fact_slugs: z.array(SlugSchema).default([]),
  related_document_slugs: z.array(SlugSchema).default([]),
  related_contradiction_ids: z.array(z.string().uuid()).default([]),
  recommended_next_step: z.string().min(10).max(600),
}).strict();
export type LitigationReviewItem = z.infer<typeof LitigationReviewItemSchema>;

const LitigationReviewLegacyContentObjectSchema = z.object({
  disputed_nodes: z.array(LitigationReviewItemSchema).max(12).default([]),
  evidence_to_obtain: z.array(LitigationReviewItemSchema).max(12).default([]),
  client_clarifications: z.array(LitigationReviewItemSchema).max(12).default([]),
  generation_metadata: WikiDocumentGenerationMetadataSchema.optional(),
}).strict();
export const LitigationReviewLegacyContentSchema = LitigationReviewLegacyContentObjectSchema;
export type LitigationReviewLegacyContent = z.infer<
  typeof LitigationReviewLegacyContentSchema
>;

export const LitigationClaimPositionDraftSchema = z.object({
  title: z.string().min(5).max(180),
  request_summary: z.string().min(20).max(900),
  asserted_by_party_slugs: z.array(SlugSchema).max(20).default([]),
  responding_party_slugs: z.array(SlugSchema).max(20).default([]),
  amount_text: z.string().max(120).nullable().default(null).describe(
    'Un seul montant : le total demandé avec sa devise, 120 caractères au plus. Le détail par poste va dans request_summary. null si le dossier ne donne pas de total.',
  ),
  status: z.enum(['asserted', 'admitted', 'contested', 'unclear']),
  response_summary: z.string().max(900).nullable().default(null),
  // La règle croisée est appliquée par `upsert_wiki_document` ; elle est dite ici parce qu'une
  // description de champ est la seule partie du schéma JSON où le modèle peut la lire (#2046).
  coverage: z.enum(['supported', 'partial', 'missing']).describe(
    'supported ou partial : au moins un slug dans related_fact_slugs ou related_document_slugs. missing : au moins une entrée dans missing_evidence.',
  ),
  related_fact_slugs: z.array(SlugSchema).max(80).default([]),
  related_document_slugs: z.array(SlugSchema).max(80).default([]),
  missing_evidence: z.array(z.string().min(5).max(400)).max(20).default([]).describe(
    "Preuves qui manquent pour étayer la demande. Au moins une entrée quand coverage vaut 'missing'.",
  ),
  recommended_next_step: z.string().min(10).max(600),
}).strict();
export type LitigationClaimPositionDraft = z.infer<
  typeof LitigationClaimPositionDraftSchema
>;

export const LitigationClaimPositionSchema = LitigationClaimPositionDraftSchema.extend({
  id: z.string().uuid(),
}).strict();
export type LitigationClaimPosition = z.infer<typeof LitigationClaimPositionSchema>;

const LitigationReviewV2DraftItemSchema = LitigationReviewItemSchema.omit({ id: true })
  .extend({ issue_kind: LitigationReviewWriteIssueKindSchema })
  .strict();
const LITIGATION_REVIEW_BUCKETS = [
  'disputed_nodes',
  'evidence_to_obtain',
  'client_clarifications',
] as const;

function addProceduralIssueViolations(
  content: Record<
    (typeof LITIGATION_REVIEW_BUCKETS)[number],
    readonly { issue_kind: LitigationReviewIssueKind }[]
  >,
  ctx: z.RefinementCtx,
): void {
  for (const bucket of LITIGATION_REVIEW_BUCKETS) {
    content[bucket].forEach((item, index) => {
      if (item.issue_kind === 'procedural_issue') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [bucket, index, 'issue_kind'],
          message: 'procedural_issue belongs to procedural review pass 6',
        });
      }
    });
  }
}

const LitigationReviewV2DraftObjectSchema = z.object({
  // Facultatif à l'entrée : le serveur pose la version au tamponnage (`stampLitigationReviewV2`),
  // l'exiger de l'agent ne produisait que des refus. Le document STOCKÉ la garde obligatoire.
  schema_version: z.literal(2).default(2),
  disputed_nodes: z.array(LitigationReviewV2DraftItemSchema).max(12).default([]),
  evidence_to_obtain: z.array(LitigationReviewV2DraftItemSchema).max(12).default([]),
  client_clarifications: z.array(LitigationReviewV2DraftItemSchema).max(12).default([]),
  claim_positions: z.array(LitigationClaimPositionDraftSchema).max(100),
}).strict();

// Pas de `superRefine` ici : l'enum d'écriture de `issue_kind` porte déjà la règle, de façon
// visible dans le schéma de l'outil.
export const LitigationReviewV2DraftSchema = LitigationReviewV2DraftObjectSchema;
export type LitigationReviewV2Draft = z.infer<typeof LitigationReviewV2DraftSchema>;
export type LitigationReviewV2DraftItem = z.infer<typeof LitigationReviewV2DraftItemSchema>;

export const LitigationReviewV2ContentSchema = LitigationReviewV2DraftObjectSchema.extend({
  schema_version: z.literal(2),
  profile: z.object({
    id: CaseWikiDomainProfileIdSchema,
    version: z.literal(1),
  }).strict(),
  disputed_nodes: z.array(LitigationReviewItemSchema).max(12),
  evidence_to_obtain: z.array(LitigationReviewItemSchema).max(12),
  client_clarifications: z.array(LitigationReviewItemSchema).max(12),
  claim_positions: z.array(LitigationClaimPositionSchema).max(100),
  generation_metadata: WikiDocumentGenerationMetadataSchema.optional(),
}).strict().superRefine(
  (content, ctx) => addProceduralIssueViolations(content, ctx),
);
export type LitigationReviewV2Content = z.infer<typeof LitigationReviewV2ContentSchema>;

/** Read contract: legacy unversioned rows or the explicitly supported V2 shape. */
export const LitigationReviewContentSchema = z.union([
  LitigationReviewV2ContentSchema,
  LitigationReviewLegacyContentSchema,
]);
export type LitigationReviewContent = z.infer<typeof LitigationReviewContentSchema>;

/**
 * Legacy write compatibility for queued or historical payload parsing. Active
 * Reviewer writes use `LitigationReviewV2DraftSchema` through review-payloads.
 */
export const LitigationReviewWriteContentSchema = LitigationReviewLegacyContentObjectSchema.superRefine(
  (content, ctx) => addProceduralIssueViolations(content, ctx),
);
export type LitigationReviewWriteContent = z.infer<
  typeof LitigationReviewWriteContentSchema
>;

// ---------------------------------------------------------------------------
// `nullities_admissibility` content
// ---------------------------------------------------------------------------

export const NullitiesAdmissibilityDomainSchema = z.enum([
  'criminal',
  'civil_commercial',
  'contract_commercial',
  'mixed',
  'unknown',
]);
export type NullitiesAdmissibilityDomain = z.infer<
  typeof NullitiesAdmissibilityDomainSchema
>;

export const NullitiesAdmissibilityCategorySchema = z.enum([
  'criminal_procedure_nullity',
  'civil_procedure_nullity',
  'substantive_contract_nullity',
  'inadmissibility',
  'non_written_clause',
  'prescription_forclusion',
  'jurisdiction_competence',
  'evidence_admissibility',
  'other',
]);
export type NullitiesAdmissibilityCategory = z.infer<
  typeof NullitiesAdmissibilityCategorySchema
>;

export const NullitiesAdmissibilityLegalTheorySchema = z.enum([
  'procedural_nullity',
  'procedural_inadmissibility',
  'contract_nullity',
  'non_written_clause',
  'prescription_or_forclusion',
  'jurisdiction_or_competence',
  'evidence_issue',
  'other',
]);
export type NullitiesAdmissibilityLegalTheory = z.infer<
  typeof NullitiesAdmissibilityLegalTheorySchema
>;

export const NullitiesAdmissibilityStatusSchema = z.enum([
  'candidate',
  'weak_signal',
  'needs_document',
  'contradicted',
  'already_raised',
]);
export type NullitiesAdmissibilityStatus = z.infer<
  typeof NullitiesAdmissibilityStatusSchema
>;

export const NullitiesRegularizationSchema = z.enum([
  'possible',
  'not_possible',
  'unknown',
  'not_applicable',
]);
export type NullitiesRegularization = z.infer<typeof NullitiesRegularizationSchema>;

export const NullitiesAdmissibilityAnalysisCompletenessSchema = z.enum([
  'complete',
  'partial',
]);
export type NullitiesAdmissibilityAnalysisCompleteness = z.infer<
  typeof NullitiesAdmissibilityAnalysisCompletenessSchema
>;

export const NullitiesAdmissibilityHypothesisOriginSchema = z.enum([
  'protocol',
  'unmapped_signal',
]);
export type NullitiesAdmissibilityHypothesisOrigin = z.infer<
  typeof NullitiesAdmissibilityHypothesisOriginSchema
>;

export const NullitiesAdmissibilityAnalysisAxisSchema = z.enum([
  'procedure_nullite',
  'procedural_admissibility',
  'action_civile',
]);
export type NullitiesAdmissibilityAnalysisAxis = z.infer<
  typeof NullitiesAdmissibilityAnalysisAxisSchema
>;

/**
 * Backend-derived reference to a scoped Case Wiki entity used to explain the
 * context projection. The future nullities orchestrator constructs these refs
 * only after loading the case-scoped repository data; model analyzers must not
 * emit `context_projection` or synthesize these identifiers.
 */
export const NullitiesContextKeyActRefSchema = z.object({
  kind: z.enum(['procedure', 'fact', 'document']),
  slug: SlugSchema,
}).strict();
export type NullitiesContextKeyActRef = z.infer<typeof NullitiesContextKeyActRefSchema>;

/** Backend-owned projection assembled from scoped repository entities. */
export const NullitiesContextProjectionSchema = z.object({
  source: z.literal('derived_from_case_procedures_case_facts_party_statuses'),
  procedure_type_ids: z.array(CaseProcedureTypeSchema).max(20),
  procedural_phases: z.array(ProceduralActPhaseSchema).max(3),
  enquete_kinds: z.array(ProceduralEnqueteKindSchema).max(4),
  client_statuses: z.array(StatutProceduralSchema).max(20),
  infraction_nature: z.enum(['contravention', 'delit', 'crime', 'unknown']),
  minorite: z.enum(['mineur', 'majeur', 'unknown']),
  date_faits: z.union([IsoDateSchema, z.literal('unknown')]),
  key_act_refs: z.array(NullitiesContextKeyActRefSchema).max(100),
}).strict();
export type NullitiesContextProjection = z.infer<typeof NullitiesContextProjectionSchema>;

export const NullitiesEvidenceExcerptSchema = z.object({
  piece_slug: SlugSchema,
  quote: z.string().trim().min(1).max(4000),
  location: z.string().trim().min(1).max(300).nullable(),
}).strict();
export type NullitiesEvidenceExcerpt = z.infer<typeof NullitiesEvidenceExcerptSchema>;

export const NullitiesCheckResultOutcomeSchema = z.enum([
  'potential_issue',
  'no_issue_found',
  'indeterminate',
]);
export type NullitiesCheckResultOutcome = z.infer<
  typeof NullitiesCheckResultOutcomeSchema
>;

export const NullitiesCheckResultSchema = z.object({
  protocol_ref_id: z.string().trim().min(3).max(180),
  outcome: NullitiesCheckResultOutcomeSchema,
  condition_assessed: z.string().trim().min(5).max(1000),
  finding: z.string().trim().min(5).max(1200),
  evidence_excerpts: z.array(NullitiesEvidenceExcerptSchema).max(20),
  hypothesis_slug: SlugSchema.nullable(),
  source_document_slugs: z.array(SlugSchema).max(20),
  missing_information: z.array(z.string().trim().min(3).max(500)).max(20),
}).strict().superRefine((value, ctx) => {
  if (
    (value.outcome === 'potential_issue' || value.outcome === 'no_issue_found')
    && value.evidence_excerpts.length === 0
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['evidence_excerpts'],
      message: `${value.outcome} requires at least one exact evidence excerpt`,
    });
  }

  for (const [index, excerpt] of value.evidence_excerpts.entries()) {
    if (!value.source_document_slugs.includes(excerpt.piece_slug)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['evidence_excerpts', index, 'piece_slug'],
        message: `evidence excerpt piece_slug '${excerpt.piece_slug}' must also appear in source_document_slugs`,
      });
    }
  }

  if (value.outcome === 'indeterminate' && value.missing_information.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['missing_information'],
      message: 'indeterminate requires explicit missing information',
    });
  }
});
export type NullitiesCheckResult = z.infer<typeof NullitiesCheckResultSchema>;

export const NullitiesFamilyActivationStatusSchema = z.enum([
  'active',
  'outside_identified_scope',
  'analysis_failed',
]);
export type NullitiesFamilyActivationStatus = z.infer<
  typeof NullitiesFamilyActivationStatusSchema
>;

/**
 * Most pieces one nullities family may hand to its analysis session. The session must read every
 * candidate piece before it may conclude, so this is a reading budget, not a display limit. The
 * activation plan enforces it at the source; the schema below only restates it.
 */
export const NULLITIES_FAMILY_MAX_SOURCE_DOCUMENTS = 50;

export const NullitiesFamilyActivationSchema = z.object({
  family: z.string().trim().min(3).max(120),
  status: NullitiesFamilyActivationStatusSchema,
  reason: z.string().trim().min(5).max(1000),
  source_document_slugs: z.array(SlugSchema).max(NULLITIES_FAMILY_MAX_SOURCE_DOCUMENTS),
  /** Optional on reads so documents published before V2 provenance remain parseable. */
  protocol_ref_ids: z.array(z.string().trim().min(3).max(180)).max(100).optional(),
}).strict();
export type NullitiesFamilyActivation = z.infer<typeof NullitiesFamilyActivationSchema>;

/** Backend-owned V2 activation provenance; analyzers never supply this field. */
export const NullitiesFamilyActivationWriteSchema = NullitiesFamilyActivationSchema.extend({
  protocol_ref_ids: z.array(z.string().trim().min(3).max(180)).max(100),
}).strict();
export type NullitiesFamilyActivationWrite = z.infer<
  typeof NullitiesFamilyActivationWriteSchema
>;

export const NullitiesAdmissibilityHypothesisSchema = z.object({
  slug: SlugSchema,
  title: z.string().min(5).max(180),
  category: NullitiesAdmissibilityCategorySchema,
  legal_theory: NullitiesAdmissibilityLegalTheorySchema,
  domain: NullitiesAdmissibilityDomainSchema,
  qualification_status: NullitiesAdmissibilityStatusSchema,
  severity: z.number().int().min(0).max(4),
  confidence: z.number().int().min(0).max(4),
  affected_act_or_clause: z.string().min(3).max(300).nullable(),
  procedural_stage: z.string().min(2).max(120).nullable(),
  /**
   * Posé par le serveur depuis `protocol_ref_ids` (un libellé par article) : même borne
   * que ce champ, sinon une hypothèse liée à 9 contrôles faisait échouer sa famille (2026-10-19).
   */
  legal_basis_candidates: z.array(z.string().min(3).max(220)).max(20).default([]),
  source_document_slugs: z.array(SlugSchema).max(20).default([]),
  related_fact_slugs: z.array(SlugSchema).max(20).default([]),
  /**
   * Legacy — pointait vers `case_arguments` (retirée le 2026-07-03). Le champ
   * reste dans le schéma car (1) il est `.strict()` et des documents persistés
   * en jsonb le portent encore, (2) `filterWikiDocumentReferences` le vide
   * désormais systématiquement avant persistance.
   */
  related_argument_ids: z.array(z.string().uuid()).max(20).default([]),
  related_procedure_ids: z.array(z.string().uuid()).max(10).default([]),
  factual_triggers: z.array(z.string().min(5).max(260)).max(10).default([]).describe(
    'Une phrase courte par déclencheur, 260 caractères au plus chacune.',
  ),
  conditions_established: z.array(z.string().min(5).max(260)).max(10).default([]),
  missing_information: z.array(z.string().min(5).max(260)).max(10).default([]),
  potential_grief: z.string().min(10).max(800).nullable(),
  admissibility_watchpoints: z.array(z.string().min(5).max(260)).max(10).default([]),
  regularization_possible: NullitiesRegularizationSchema,
  risk_summary: z.string().min(20).max(900),
  recommended_next_step: z.string().min(10).max(600),
  display_formulation: z.string().min(10).max(260).describe(
    "Une phrase affichée à l'avocat, 260 caractères au plus. Le développement va dans risk_summary.",
  ),
  /** Optional on reads so pre-V2 JSONB documents remain parseable. */
  axis: NullitiesAdmissibilityAnalysisAxisSchema.optional(),
  origin: NullitiesAdmissibilityHypothesisOriginSchema.optional(),
  protocol_ref_ids: z.array(z.string().trim().min(3).max(180)).max(20).optional(),
}).strict();
export type NullitiesAdmissibilityHypothesis = z.infer<
  typeof NullitiesAdmissibilityHypothesisSchema
>;

/**
 * Strict V2 hypothesis written by Review. Historical reads deliberately use
 * `NullitiesAdmissibilityHypothesisSchema`, where the V2 provenance is optional.
 */
export const NullitiesAdmissibilityWriteHypothesisSchema =
  NullitiesAdmissibilityHypothesisSchema.extend({
    axis: NullitiesAdmissibilityAnalysisAxisSchema,
    origin: NullitiesAdmissibilityHypothesisOriginSchema,
    protocol_ref_ids: z.array(z.string().trim().min(3).max(180)).max(20),
  }).strict().superRefine((value, ctx) => {
    if (value.origin === 'protocol' && value.protocol_ref_ids.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['protocol_ref_ids'],
        message: 'protocol hypotheses require at least one protocol_ref_id',
      });
    }

    if (value.origin !== 'unmapped_signal') return;

    if (value.protocol_ref_ids.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['protocol_ref_ids'],
        message: 'unmapped_signal hypotheses cannot claim protocol_ref_ids',
      });
    }
    if (value.legal_basis_candidates.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['legal_basis_candidates'],
        message: 'unmapped_signal hypotheses cannot provide a free legal basis',
      });
    }
    if (
      value.source_document_slugs.length === 0
      && value.related_fact_slugs.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source_document_slugs'],
        message: 'unmapped_signal hypotheses require a dossier document or fact reference',
      });
    }
    if (
      value.qualification_status !== 'weak_signal'
      && value.qualification_status !== 'needs_document'
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['qualification_status'],
        message: 'unmapped_signal qualification_status must be weak_signal or needs_document',
      });
    }
  });
export type NullitiesAdmissibilityWriteHypothesis = z.infer<
  typeof NullitiesAdmissibilityWriteHypothesisSchema
>;

export const ProceduralControlEvidenceSchema = z.object({
  piece_slug: SlugSchema,
  exact_quote: z.string().min(5).max(2_000),
  relevance: z.string().min(5).max(600),
}).strict();
export type ProceduralControlEvidence = z.infer<typeof ProceduralControlEvidenceSchema>;

export const ProceduralControlLegalBasisSnapshotSchema = z.object({
  id: z.string().min(3).max(180),
  label: z.string().min(3).max(240),
  source_url: z.string().url(),
  verified_at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
}).strict();
export type ProceduralControlLegalBasisSnapshot = z.infer<
  typeof ProceduralControlLegalBasisSnapshotSchema
>;

export const ProceduralControlOutcomeSchema = z.enum([
  'potential_issue',
  'no_issue_found',
  'indeterminate',
]);
export type ProceduralControlOutcome = z.infer<typeof ProceduralControlOutcomeSchema>;

const ProceduralControlCheckResultV3BaseSchema = z.object({
  check_id: z.string().min(3).max(180),
  label: z.string().min(3).max(240),
  outcome: ProceduralControlOutcomeSchema,
  finding: z.string().min(5).max(2_000),
  legal_basis: ProceduralControlLegalBasisSnapshotSchema,
  evidence_excerpts: z.array(ProceduralControlEvidenceSchema).max(20),
  source_document_slugs: z.array(SlugSchema).max(100),
  missing_information: z.array(z.string().min(3).max(500)).max(30),
  recommended_next_step: z.string().min(5).max(800),
}).strict();

/**
 * A definitive conclusion must remain auditable from the persisted JSONB
 * snapshot. Missing a source is an abstention, not a negative or positive
 * finding, so it is represented as `indeterminate` with what is still needed.
 */
function validateProceduralControlCheckResultV3(
  result: z.infer<typeof ProceduralControlCheckResultV3BaseSchema>,
  ctx: z.RefinementCtx,
): void {
  if (result.outcome === 'indeterminate' && result.missing_information.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['missing_information'],
      message: 'indeterminate requires missing information',
    });
  }
  if (result.outcome !== 'indeterminate' && result.evidence_excerpts.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['evidence_excerpts'],
      message: `${result.outcome} requires an exact source excerpt`,
    });
  }

  // A citation outside this snapshot would make a persisted conclusion unauditable.
  for (const [index, excerpt] of result.evidence_excerpts.entries()) {
    if (!result.source_document_slugs.includes(excerpt.piece_slug)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['evidence_excerpts', index, 'piece_slug'],
        message: `evidence excerpt piece_slug '${excerpt.piece_slug}' must also appear in source_document_slugs`,
      });
    }
  }
}

export const ProceduralControlCheckResultV3Schema =
  ProceduralControlCheckResultV3BaseSchema.superRefine(
    validateProceduralControlCheckResultV3,
  );
export type ProceduralControlCheckResultV3 = z.infer<
  typeof ProceduralControlCheckResultV3Schema
>;

export const ProceduralControlFamilyResultV3Schema = z.object({
  id: z.string().min(2).max(120),
  label: z.string().min(2).max(160),
  status: z.enum(['active', 'outside_identified_scope', 'analysis_failed']),
  reason: z.string().min(2).max(600),
  check_results: z.array(ProceduralControlCheckResultV3Schema).max(200),
}).strict();
export type ProceduralControlFamilyResultV3 = z.infer<
  typeof ProceduralControlFamilyResultV3Schema
>;

export const ProceduralControlReviewV3Schema = z.object({
  schema_version: z.literal(3),
  profile_id: CaseWikiDomainProfileIdSchema,
  control_pack: z.object({
    id: z.string().min(3).max(120),
    version: z.number().int().positive(),
    fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  }).strict(),
  surface: z.object({
    label: z.string().min(2),
    subtitle: z.string().min(2),
  }).strict(),
  families: z.array(ProceduralControlFamilyResultV3Schema).max(50),
  analysis_completeness: NullitiesAdmissibilityAnalysisCompletenessSchema,
  generation_metadata: WikiDocumentGenerationMetadataSchema.optional(),
}).strict();
export type ProceduralControlReviewV3 = z.infer<
  typeof ProceduralControlReviewV3Schema
>;

/** Compatibility export for P1 readers while consumers adopt the final name. */
export const MultiProfileProceduralControlReviewV3Schema =
  ProceduralControlReviewV3Schema;
export type MultiProfileProceduralControlReviewV3 = ProceduralControlReviewV3;

export const NullitiesAdmissibilityLegacyContentSchema = z.object({
  summary: z.string().min(20).max(1200),
  hypotheses: z.array(NullitiesAdmissibilityHypothesisSchema).max(20).default([]),
  /** Optional V2 fields preserve historical JSONB reads. New writes use the strict schema below. */
  analysis_completeness: NullitiesAdmissibilityAnalysisCompletenessSchema.optional(),
  context_projection: NullitiesContextProjectionSchema.optional(),
  family_activation: z.array(NullitiesFamilyActivationSchema).max(30).optional(),
  check_results: z.array(NullitiesCheckResultSchema).max(200).optional(),
  /** Backend-owned audit snapshot; absent only on historical document versions. */
  protocol_reference_snapshot: NullitiesProtocolReferenceSnapshotSchema.optional(),
  generation_metadata: WikiDocumentGenerationMetadataSchema.optional(),
}).strict();
export type NullitiesAdmissibilityLegacyContent = z.infer<
  typeof NullitiesAdmissibilityLegacyContentSchema
>;
/**
 * Public readers must accept persisted historical and Penal V2 documents while
 * applying the stricter autonomous contract to every V3 document.
 */
export const ProceduralReviewReadContentSchema = z.union([
  ProceduralControlReviewV3Schema,
  NullitiesAdmissibilityLegacyContentSchema,
]);
export type ProceduralReviewReadContent = z.infer<
  typeof ProceduralReviewReadContentSchema
>;

export const NullitiesAdmissibilityContentSchema = ProceduralReviewReadContentSchema;
export type NullitiesAdmissibilityContent = z.infer<
  typeof NullitiesAdmissibilityContentSchema
>;

/**
 * Current model-facing pass-6 write boundary. It intentionally excludes every
 * backend-owned field (`context_projection`, `generation_metadata` and the other
 * V2-only fields), while remaining compatible with historical summary+hypotheses output.
 */
/**
 * Hypothèse telle qu'un agent l'écrit. Son `slug` est un identifiant que l'agent invente : une
 * majuscule ou un accent y est normalisé au lieu d'être refusé (48 refus en 7 jours, #2046).
 * Le schéma de lecture garde `SlugSchema`, qui ne réécrit jamais un slug stocké.
 */
const NullitiesAdmissibilityLegacyWriteHypothesisSchema =
  NullitiesAdmissibilityHypothesisSchema.extend({ slug: SlugInputSchema }).strict();

export const NullitiesAdmissibilityLegacyWriteContentSchema = z.object({
  summary: z.string().min(20).max(1200).describe(
    "Synthèse du document lue par l'avocat, 20 à 1200 caractères. Champ obligatoire de content, distinct du summary de finish_review_pass.",
  ),
  hypotheses: z.array(NullitiesAdmissibilityLegacyWriteHypothesisSchema).max(20).default([]),
}).strict();
export type NullitiesAdmissibilityLegacyWriteContent = z.infer<
  typeof NullitiesAdmissibilityLegacyWriteContentSchema
>;

/**
 * Backend-orchestrator write contract for V2 Review output. Model analyzers do
 * not emit this shape directly: the orchestrator supplies `context_projection`
 * from scoped repository data, then validates this complete object immediately
 * before direct persistence. This shared schema validates shape and internal
 * links only: before the final parse, the orchestrator must filter every
 * model-provided document/fact reference against the current case sets.
 */
export const NullitiesAdmissibilityWriteContentSchema = z.object({
  summary: z.string().min(20).max(1200),
  analysis_completeness: NullitiesAdmissibilityAnalysisCompletenessSchema,
  context_projection: NullitiesContextProjectionSchema,
  family_activation: z.array(NullitiesFamilyActivationWriteSchema).max(30),
  check_results: z.array(NullitiesCheckResultSchema).max(200),
  hypotheses: z.array(NullitiesAdmissibilityWriteHypothesisSchema).max(20),
  protocol_reference_snapshot: NullitiesProtocolReferenceSnapshotSchema,
  generation_metadata: WikiDocumentGenerationMetadataSchema.optional(),
}).strict().superRefine((value, ctx) => {
  const hasFailedFamily = value.family_activation.some(
    (activation) => activation.status === 'analysis_failed',
  );

  if (value.analysis_completeness === 'complete' && hasFailedFamily) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['analysis_completeness'],
      message: "analysis_completeness must be 'partial' when a family has status 'analysis_failed'",
    });
  }
  if (value.analysis_completeness === 'partial' && !hasFailedFamily) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['analysis_completeness'],
      message: "analysis_completeness must be 'complete' when no family has status 'analysis_failed'",
    });
  }

  const hypothesisBySlug = new Map<
    string,
    (typeof value.hypotheses)[number]
  >();
  for (const [index, hypothesis] of value.hypotheses.entries()) {
    if (hypothesisBySlug.has(hypothesis.slug)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hypotheses', index, 'slug'],
        message: `hypothesis slug '${hypothesis.slug}' must be unique within nullities_admissibility content`,
      });
      continue;
    }
    hypothesisBySlug.set(hypothesis.slug, hypothesis);
  }

  for (const [index, checkResult] of value.check_results.entries()) {
    if (checkResult.hypothesis_slug === null) continue;

    const hypothesis = hypothesisBySlug.get(checkResult.hypothesis_slug);
    if (!hypothesis) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['check_results', index, 'hypothesis_slug'],
        message: `check result protocol_ref_id '${checkResult.protocol_ref_id}' references missing hypothesis_slug '${checkResult.hypothesis_slug}'`,
      });
      continue;
    }

    if (
      hypothesis.origin === 'protocol'
      && !hypothesis.protocol_ref_ids.includes(checkResult.protocol_ref_id)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['check_results', index, 'protocol_ref_id'],
        message: `protocol hypothesis '${hypothesis.slug}' must include linked check protocol_ref_id '${checkResult.protocol_ref_id}'`,
      });
    }
  }
});
export type NullitiesAdmissibilityWriteContent = z.infer<
  typeof NullitiesAdmissibilityWriteContentSchema
>;

// ---------------------------------------------------------------------------
// Wiki document — row complet
// ---------------------------------------------------------------------------

/** Row complet de `case_wiki_documents`. */
export const WikiDocumentSchema = z.object({
  id: z.string().uuid(),
  case_id: z.string().uuid(),
  type: WikiDocumentTypeSchema,
  version: z.number().int().min(1),
  /**
   * Forme polymorphe : les documents narratifs retournent `{ markdown, ... }`,
   * et `litigation_review` / `nullities_admissibility` retournent une revue
   * structurée. Le consommateur discrimine sur `type`.
   */
  content: z.union([
    MarkdownDocContentSchema,
    LitigationReviewContentSchema,
    NullitiesAdmissibilityContentSchema,
  ]),
  generated_by: z.string(),
  generated_at: z.string().datetime(),
});
export type WikiDocument = z.infer<typeof WikiDocumentSchema>;

// ---------------------------------------------------------------------------
// Latest-by-type — exposé dans le bundle
// ---------------------------------------------------------------------------

/**
 * Latest version de chaque type actif, exposé dans le `CaseWikiBundle.documents`.
 * Tous nullable : un dossier sans Reviewer run aura tout à null.
 */
export const WikiDocumentsBundleSchema = z.object({
  snapshot: z.object({
    version: z.number().int().min(1),
    content: MarkdownDocContentSchema,
    generated_at: z.string().datetime(),
  }).nullable(),
  litigationReview: z.object({
    version: z.number().int().min(1),
    content: LitigationReviewContentSchema,
    generated_at: z.string().datetime(),
  }).nullable().optional().default(null),
  nullitiesAdmissibility: z.object({
    version: z.number().int().min(1),
    content: NullitiesAdmissibilityContentSchema,
    generated_at: z.string().datetime(),
  }).nullable().optional().default(null),
});
export type WikiDocumentsBundle = z.infer<typeof WikiDocumentsBundleSchema>;
